/*
 * Delivery country lookup
 * - Unsupported countries: UNSUPPORTED_COUNTRIES below
 * - Country shipping copy: assets/delivery-countries-data.json
 * - Extended terms (per country, English): assets/shipping-returns-terms-data.json
 */

const UNSUPPORTED_MESSAGE =
  "We prefer to offer a fully 'duties paid' shipping service, so that all customers know the final amount they will pay at checkout. We are currently unable to do this in {country} and therefore have disabled shipping to {country} on our website. If you would like to purchase from one of these countries please contact us to discuss your request.";

const UNSUPPORTED_COUNTRIES = [
  { display: 'Afghanistan', aliases: ['afghanistan'] },
  { display: 'Albania', aliases: ['albania'] },
  { display: 'Andorra', aliases: ['andorra'] },
  { display: 'Armenia', aliases: ['armenia'] },
  { display: 'Azerbaijan', aliases: ['azerbaijan'] },
  { display: 'Belarus', aliases: ['belarus'] },
  { display: 'Benin', aliases: ['benin'] },
  { display: 'Bosnia-Herzegovina', aliases: ['bosnia herzegovina', 'bosnia-herzegovina', 'bosnia'] },
  { display: 'Burkina Faso', aliases: ['burkina faso'] },
  { display: 'Burma/Myanmar', aliases: ['burma', 'myanmar', 'burma myanmar'] },
  { display: 'Central African Republic', aliases: ['central african republic'] },
  { display: 'Chad', aliases: ['chad'] },
  { display: 'Cyprus', aliases: ['cyprus'] },
  { display: 'Equatorial Guinea', aliases: ['equatorial guinea'] },
  { display: 'Eritrea', aliases: ['eritrea'] },
  { display: 'Faroe Islands', aliases: ['faroe islands'] },
  { display: 'Georgia', aliases: ['georgia'] },
  { display: 'Greenland', aliases: ['greenland'] },
  { display: 'Guinea', aliases: ['guinea'] },
  { display: 'Guinea Bissau', aliases: ['guinea bissau', 'guinea-bissau'] },
  { display: 'Iran', aliases: ['iran'] },
  { display: 'Jersey', aliases: ['jersey'] },
  { display: 'Kazakhstan', aliases: ['kazakhstan'] },
  { display: 'Kiribati', aliases: ['kiribati'] },
  { display: 'Kyrgyzstan', aliases: ['kyrgyzstan'] },
  { display: 'Lesotho', aliases: ['lesotho'] },
  { display: 'Macedonia', aliases: ['macedonia', 'north macedonia'] },
  { display: 'Mali', aliases: ['mali'] },
  { display: 'Marshall Islands', aliases: ['marshall islands'] },
  { display: 'Mauritania', aliases: ['mauritania'] },
  { display: 'Mayotte', aliases: ['mayotte'] },
  { display: 'Micronesia', aliases: ['micronesia'] },
  { display: 'Moldova', aliases: ['moldova'] },
  { display: 'Montenegro', aliases: ['montenegro'] },
  { display: 'Myanmar', aliases: ['myanmar'] },
  { display: 'Niger', aliases: ['niger'] },
  { display: 'Palestine', aliases: ['palestine'] },
  { display: 'Russia', aliases: ['russia', 'russian federation'] },
  { display: 'Sao Tome e Principe', aliases: ['sao tome e principe', 'sao tome and principe'] },
  { display: 'Seychelles', aliases: ['seychelles'] },
  { display: 'Sierra Leone', aliases: ['sierra leone'] },
  { display: 'Solomon Islands', aliases: ['solomon islands'] },
  { display: 'Somalia', aliases: ['somalia'] },
  { display: 'St Pierre and Miquelon', aliases: ['st pierre and miquelon', 'saint pierre and miquelon'] },
  { display: 'Sudan', aliases: ['sudan'] },
  { display: 'Syria', aliases: ['syria'] },
  { display: 'Tajikistan', aliases: ['tajikistan'] },
  { display: 'Türkiye', aliases: ['turkiye', 'turkey', 'republic of turkiye', 'republic of turkey'] },
  { display: 'Turkmenistan', aliases: ['turkmenistan'] },
];

const COUNTRY_ALIASES = {
  'United States': ['usa', 'us', 'u.s.', 'u.s.a.', 'america', 'united states of america'],
  'Ireland (Republic of)': ['ireland', 'republic of ireland', 'eire'],
  'South Korea': ['korea', 'republic of korea'],
  'United Arab Emirates': ['uae', 'emirates'],
  'Czech Republic': ['czechia'],
  'Hong Kong': ['hk'],
  'New Zealand': ['nz', 'aotearoa'],
  'United Kingdom': ['uk', 'u.k.', 'great britain', 'britain', 'england', 'scotland', 'wales', 'northern ireland'],
  'St Barthélemy': ['saint barthelemy', 'st barth', 'st. barthélemy'],
  Guernsey: ['alderney', 'sark', 'bailiwick of guernsey'],
};

// Countries that ship on another country's terms. They get their own
// suggestion so the search reflects what was typed, but open the shared page.
const SHARED_PAGE_COUNTRIES = {
  Liechtenstein: { page: 'Switzerland', aliases: ['furstentum liechtenstein', 'principality of liechtenstein'] },
};

const EXTRA_DEFAULT_COUNTRIES = [
  'Serbia',
  'Ukraine',
  'Vatican City',
];

const SECTION_HEADINGS = {
  intro: 'Shipping',
  rates: 'Shipping Rates',
  delivery_times: 'Estimated delivery times',
  tax_refund: 'Taxes & Duties',
  sales_tax: 'Sales Tax',
  import_tariffs: 'Import Tariffs',
  large_orders: 'Orders over $2,500',
  facility: 'Our facility',
  withdrawal: 'Right of withdrawal',
  returns: 'Returns',
};

const COLLAPSIBLE_SECTION_IDS = new Set(['sales_tax', 'import_tariffs', 'large_orders']);

// EU/EEA countries show their Returns and accordion content expanded by default so the
// consumer-rights information is visible without interaction.
const EXPANDED_BY_DEFAULT_COUNTRIES = new Set([
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Iceland',
  'Ireland (Republic of)',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Norway',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
]);

const RETURNS_SUBSECTION_HEADINGS = {
  en: {
    condition: 'Condition of returned items',
    refund_credit: 'Refund or Store Credit',
    free_return_label: 'Free return shipping label',
    exchanges: 'Exchanges',
  },
  de: {
    condition: 'Zustand der zurückgesendeten Artikel',
    refund_credit: 'Erstattung oder Guthaben',
    free_return_label: 'Kostenloses Rücksendeetikett',
    exchanges: 'Umtausch',
  },
  nl: {
    condition: 'Staat van geretourneerde artikelen',
    refund_credit: 'Terugbetaling of tegoed',
    free_return_label: 'Gratis retourlabel',
    exchanges: 'Ruilen',
  },
  fr: {
    condition: 'État des articles retournés',
    refund_credit: 'Remboursement ou avoir',
    free_return_label: 'Étiquette de retour gratuite',
    exchanges: 'Échanges',
  },
  bg: {
    condition: 'Състояние на върнатите артикули',
    refund_credit: 'Възстановяване или ваучер',
    free_return_label: 'Безплатен етикет за връщане',
    exchanges: 'Замяна',
  },
  es: {
    condition: 'Estado de los artículos devueltos',
    refund_credit: 'Reembolso o saldo de tienda',
    free_return_label: 'Etiqueta de devolución gratuita',
    exchanges: 'Cambios',
  },
  'zh-CN': {
    condition: '退回商品的状态',
    refund_credit: '退款或店铺积分',
    free_return_label: '免费退货标签',
    exchanges: '换货',
  },
  cs: {
    condition: 'Stav vráceného zboží',
    refund_credit: 'Vrácení peněz nebo kredit',
    free_return_label: 'Bezplatný zpáteční štítek',
    exchanges: 'Výměna',
  },
  da: {
    condition: 'De returnerede varers stand',
    refund_credit: 'Refusion eller butikskredit',
    free_return_label: 'Gratis returlabel',
    exchanges: 'Ombytning',
  },
  fi: {
    condition: 'Palautettavien tuotteiden kunto',
    refund_credit: 'Hyvitys tai myymäläkredit',
    free_return_label: 'Ilmainen palautustarra',
    exchanges: 'Vaihto',
  },
  el: {
    condition: 'Κατάσταση επιστρεφόμενων ειδών',
    refund_credit: 'Επιστροφή χρημάτων ή πίστωση',
    free_return_label: 'Δωρεάν ετικέτα επιστροφής',
    exchanges: 'Ανταλλαγές',
  },
  'zh-TW': {
    condition: '退回商品的狀態',
    refund_credit: '退款或商店點數',
    free_return_label: '免費退貨標籤',
    exchanges: '換貨',
  },
  hu: {
    condition: 'A visszaküldött termékek állapota',
    refund_credit: 'Visszatérítés vagy üzleti jóváírás',
    free_return_label: 'Ingyenes visszaküldési címke',
    exchanges: 'Csere',
  },
  is: {
    condition: 'Ástand endursendra vara',
    refund_credit: 'Endurgreiðsla eða inneign',
    free_return_label: 'Frír skilamiði',
    exchanges: 'Skipti',
  },
  he: {
    condition: 'מצב הפריטים המוחזרים',
    refund_credit: 'החזר כספי או זיכוי',
    free_return_label: 'תווית החזרה חינם',
    exchanges: 'החלפות',
  },
  it: {
    condition: 'Condizione degli articoli resi',
    refund_credit: 'Rimborso o credito negozio',
    free_return_label: 'Etichetta di reso gratuita',
    exchanges: 'Cambi',
  },
  ko: {
    condition: '반품 상품 상태',
    refund_credit: '환불 또는 스토어 크레딧',
    free_return_label: '무료 반품 라벨',
    exchanges: '교환',
  },
  ar: {
    condition: 'حالة المنتجات المُرجَعة',
    refund_credit: 'استرداد المبلغ أو رصيد المتجر',
    free_return_label: 'ملصق إرجاع مجاني',
    exchanges: 'الاستبدال',
  },
  lb: {
    condition: 'Zoustand vun de zréckgeschéckten Artikelen',
    refund_credit: 'Erstattung oder Guttuewen',
    free_return_label: 'Gratis Réckschéckungs-Etikett',
    exchanges: 'Ëmtausch',
  },
  ms: {
    condition: 'Keadaan barang yang dipulangkan',
    refund_credit: 'Bayaran balik atau Kredit Kedai',
    free_return_label: 'Label pemulangan percuma',
    exchanges: 'Pertukaran',
  },
  no: {
    condition: 'Tilstanden på returnerte varer',
    refund_credit: 'Refusjon eller butikkkreditt',
    free_return_label: 'Gratis returetikett',
    exchanges: 'Bytte',
  },
  tl: {
    condition: 'Kondisyon ng mga isinauling item',
    refund_credit: 'Refund o Store Credit',
    free_return_label: 'Libreng return shipping label',
    exchanges: 'Mga Palitan',
  },
  pl: {
    condition: 'Stan zwracanych produktów',
    refund_credit: 'Zwrot pieniędzy lub kredyt sklepowy',
    free_return_label: 'Bezpłatna etykieta zwrotna',
    exchanges: 'Wymiana',
  },
  pt: {
    condition: 'Estado dos artigos devolvidos',
    refund_credit: 'Reembolso ou crédito na loja',
    free_return_label: 'Etiqueta de devolução gratuita',
    exchanges: 'Trocas',
  },
  sk: {
    condition: 'Stav vráteného tovaru',
    refund_credit: 'Vrátenie peňazí alebo kredit',
    free_return_label: 'Bezplatný spiatočný štítok',
    exchanges: 'Výmena',
  },
  sl: {
    condition: 'Stanje vrnjenih izdelkov',
    refund_credit: 'Vračilo denarja ali dobropis',
    free_return_label: 'Brezplačna nalepka za vračilo',
    exchanges: 'Zamenjave',
  },
  sv: {
    condition: 'De returnerade varornas skick',
    refund_credit: 'Återbetalning eller butikskredit',
    free_return_label: 'Gratis returetikett',
    exchanges: 'Byten',
  },
  th: {
    condition: 'สภาพของสินค้าที่ส่งคืน',
    refund_credit: 'การคืนเงินหรือเครดิตร้านค้า',
    free_return_label: 'ฉลากส่งคืนฟรี',
    exchanges: 'การเปลี่ยนสินค้า',
  },
  hr: {
    condition: 'Stanje vraćenih artikala',
    refund_credit: 'Povrat novca ili kredit u trgovini',
    free_return_label: 'Besplatna naljepnica za povrat',
    exchanges: 'Zamjene',
  },
  et: {
    condition: 'Tagastatavate toodete seisukord',
    refund_credit: 'Tagasimakse või poekrediit',
    free_return_label: 'Tasuta tagastussilt',
    exchanges: 'Vahetused',
  },
  lt: {
    condition: 'Grąžinamų prekių būklė',
    refund_credit: 'Pinigų grąžinimas arba parduotuvės kreditas',
    free_return_label: 'Nemokama grąžinimo etiketė',
    exchanges: 'Keitimai',
  },
  ro: {
    condition: 'Starea articolelor returnate',
    refund_credit: 'Rambursare sau credit în magazin',
    free_return_label: 'Etichetă de retur gratuită',
    exchanges: 'Schimburi',
  },
  lv: {
    condition: 'Atgriezto preču stāvoklis',
    refund_credit: 'Naudas atmaksa vai veikala kredīts',
    free_return_label: 'Bezmaksas atgriešanas etiķete',
    exchanges: 'Apmaiņa',
  },
};

const HEADINGS_BY_LANGUAGE = {
  en: SECTION_HEADINGS,
  de: {
    intro: 'Versand',
    rates: 'Versandkosten',
    tax_refund: 'Steuern & Zölle',
    withdrawal: 'Widerrufsrecht',
  },
  fr: {
    intro: 'Livraison',
    rates: "Tarifs d'expédition",
    tax_refund: 'Taxes et droits de douane',
    withdrawal: 'Droit de rétractation',
  },
  nl: {
    intro: 'Verzending',
    rates: 'Verzendkosten',
    tax_refund: 'Belastingen & Douanerechten',
    withdrawal: 'Herroepingsrecht',
  },
  bg: {
    intro: 'Доставка',
    rates: 'Тарифи за доставка',
    tax_refund: 'Данъци и мита',
    withdrawal: 'Право на отказ',
  },
  es: {
    intro: 'Envíos',
    rates: 'Tarifas de envío',
    tax_refund: 'Impuestos y aranceles',
    withdrawal: 'Derecho de desistimiento',
  },
  'zh-CN': {
    intro: '配送',
    rates: '运费',
    tax_refund: '税费与关税',
  },
  cs: {
    intro: 'Doprava',
    rates: 'Ceny dopravy',
    tax_refund: 'Daně a cla',
    withdrawal: 'Právo na odstoupení od smlouvy',
  },
  da: {
    intro: 'Forsendelse',
    rates: 'Forsendelsespriser',
    tax_refund: 'Skatter og afgifter',
    withdrawal: 'Fortrydelsesret',
  },
  fi: {
    intro: 'Toimitus',
    rates: 'Toimituskulut',
    tax_refund: 'Verot ja tullit',
    withdrawal: 'Peruuttamisoikeus',
  },
  el: {
    intro: 'Αποστολή',
    rates: 'Χρεώσεις αποστολής',
    tax_refund: 'Φόροι και δασμοί',
    withdrawal: 'Δικαίωμα υπαναχώρησης',
  },
  'zh-TW': {
    intro: '運送',
    rates: '運費',
    tax_refund: '稅費與關稅',
  },
  hu: {
    intro: 'Szállítás',
    rates: 'Szállítási díjak',
    tax_refund: 'Adók és vámok',
    withdrawal: 'Elállási jog',
  },
  is: {
    intro: 'Sending',
    rates: 'Sendingarkostnaður',
    tax_refund: 'Skattar og tollar',
  },
  he: {
    intro: 'משלוח',
    rates: 'תעריפי משלוח',
    tax_refund: 'מסים ומכסים',
  },
  it: {
    intro: 'Spedizione',
    rates: 'Tariffe di spedizione',
    tax_refund: 'Tasse e dazi',
    withdrawal: 'Diritto di recesso',
  },
  ko: {
    intro: '배송',
    rates: '배송 요금',
    tax_refund: '세금 및 관세',
  },
  ar: {
    intro: 'الشحن',
    rates: 'أسعار الشحن',
    tax_refund: 'الضرائب والرسوم الجمركية',
  },
  lb: {
    intro: 'Versand',
    rates: 'Versandkäschten',
    tax_refund: 'Steieren & Zoll',
    withdrawal: 'Récktrëttsrecht',
  },
  ms: {
    intro: 'Penghantaran',
    rates: 'Kadar penghantaran',
    tax_refund: 'Cukai & Duti',
  },
  no: {
    intro: 'Frakt',
    rates: 'Fraktpriser',
    tax_refund: 'Skatter og avgifter',
  },
  tl: {
    intro: 'Pagpapadala',
    rates: 'Mga Halaga ng Pagpapadala',
    tax_refund: 'Mga Buwis at Tungkulin',
  },
  pl: {
    intro: 'Wysyłka',
    rates: 'Koszty wysyłki',
    tax_refund: 'Podatki i cła',
    withdrawal: 'Prawo odstąpienia od umowy',
  },
  pt: {
    intro: 'Envio',
    rates: 'Tarifas de envio',
    tax_refund: 'Impostos e taxas alfandegárias',
    withdrawal: 'Direito de resolução',
  },
  sk: {
    intro: 'Doprava',
    rates: 'Ceny dopravy',
    tax_refund: 'Dane a clá',
    withdrawal: 'Právo na odstúpenie od zmluvy',
  },
  sl: {
    intro: 'Dostava',
    rates: 'Cene dostave',
    tax_refund: 'Davki in carine',
    withdrawal: 'Pravica do odstopa',
  },
  sv: {
    intro: 'Frakt',
    rates: 'Fraktpriser',
    tax_refund: 'Skatter och tullar',
    withdrawal: 'Ångerrätt',
  },
  th: {
    intro: null,
    rates: null,
    tax_refund: null,
  },
  hr: {
    intro: 'Dostava',
    rates: 'Cijene dostave',
    tax_refund: 'Porezi i carine',
    delivery_times: 'Procijenjeni rokovi dostave',
    returns: 'Povrati',
    withdrawal: 'Pravo na odustanak',
  },
  et: {
    intro: 'Tarne',
    rates: 'Tarnehinnad',
    tax_refund: 'Maksud ja tollimaksud',
    delivery_times: 'Eeldatavad tarneajad',
    returns: 'Tagastused',
    withdrawal: 'Taganemisõigus',
  },
  lt: {
    intro: 'Pristatymas',
    rates: 'Pristatymo kainos',
    tax_refund: 'Mokesčiai ir muitai',
    delivery_times: 'Numatomi pristatymo terminai',
    returns: 'Grąžinimai',
    withdrawal: 'Teisė atsisakyti sutarties',
  },
  ro: {
    intro: 'Livrare',
    rates: 'Tarife de livrare',
    tax_refund: 'Taxe și taxe vamale',
    delivery_times: 'Termene de livrare estimate',
    returns: 'Returnări',
    withdrawal: 'Dreptul de retragere',
  },
  lv: {
    intro: 'Piegāde',
    rates: 'Piegādes cenas',
    tax_refund: 'Nodokļi un muitas nodevas',
    delivery_times: 'Paredzamie piegādes termiņi',
    returns: 'Atgriešana',
    withdrawal: 'Atteikuma tiesības',
  },
};

function resolveSectionHeading(section, languageCode) {
  if (Object.prototype.hasOwnProperty.call(section, 'heading')) {
    return section.heading;
  }

  const languageHeadings = HEADINGS_BY_LANGUAGE[languageCode];
  if (languageHeadings && Object.prototype.hasOwnProperty.call(languageHeadings, section.id)) {
    return languageHeadings[section.id];
  }

  return SECTION_HEADINGS[section.id] || null;
}

function resolveSubsectionHeading(subsection, languageCode) {
  if (Object.prototype.hasOwnProperty.call(subsection, 'heading')) {
    return subsection.heading;
  }

  const languageHeadings = RETURNS_SUBSECTION_HEADINGS[languageCode];
  if (languageHeadings && Object.prototype.hasOwnProperty.call(languageHeadings, subsection.id)) {
    return languageHeadings[subsection.id];
  }

  return RETURNS_SUBSECTION_HEADINGS.en[subsection.id] || null;
}

const RETURNS_CONTACT_URL = 'https://therealmccoys.com/pages/contact-us';
const WITHDRAWAL_FORM_URL = 'https://therealmccoys.com/pages/eu-withdrawal-form';
const WITHDRAWAL_CTA_LABEL = 'Withdraw from Contract';

function enrichWindowSubsection(subsection, contactUrl) {
  const enriched = {
    id: subsection.id,
    body: subsection.body,
  };

  if (subsection.body_html) {
    enriched.body_html = subsection.body_html;
  }

  if (subsection.cta) {
    enriched.cta = subsection.cta;
  }

  if (!enriched.body_html && enriched.cta?.label && subsection.body?.includes(enriched.cta.label)) {
    const url = enriched.cta.url || contactUrl || RETURNS_CONTACT_URL;
    enriched.body_html = subsection.body.replace(
      enriched.cta.label,
      `<a href="${url}">${enriched.cta.label}</a>`
    );
  }

  return enriched;
}

function enrichSubsections(subsections, languageCode, contactUrl) {
  return subsections.map((subsection) => ({
    ...enrichWindowSubsection(subsection, contactUrl),
    heading: resolveSubsectionHeading(subsection, languageCode),
  }));
}

function normalizeCountry(value) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function countryMatches(normalized, alias) {
  return (
    normalized === alias ||
    normalized.startsWith(`${alias} `) ||
    normalized.endsWith(` ${alias}`) ||
    normalized.includes(` ${alias} `) ||
    normalized.includes(alias) ||
    alias.includes(normalized)
  );
}

function getSearchTerms(label, extraAliases = []) {
  return [normalizeCountry(label), ...extraAliases.map(normalizeCountry)];
}

function buildSearchableCountries(deliveryData) {
  const countries = [];

  UNSUPPORTED_COUNTRIES.forEach((entry) => {
    countries.push({
      label: entry.display,
      type: 'unsupported',
      searchTerms: [normalizeCountry(entry.display), ...entry.aliases],
    });
  });

  deliveryData.countries.forEach((entry) => {
    countries.push({
      label: entry.country,
      type: 'country',
      entry,
      searchTerms: getSearchTerms(entry.country, COUNTRY_ALIASES[entry.country] || []),
    });
  });

  Object.entries(SHARED_PAGE_COUNTRIES).forEach(([label, { page, aliases }]) => {
    const entry = deliveryData.countries.find((country) => country.country === page);
    if (!entry) return;

    countries.push({
      label,
      type: 'country',
      entry,
      searchTerms: getSearchTerms(label, aliases),
    });
  });

  EXTRA_DEFAULT_COUNTRIES.forEach((label) => {
    countries.push({
      label,
      type: 'default',
      searchTerms: getSearchTerms(label),
    });
  });

  const seen = new Set();
  return countries.filter((country) => {
    if (seen.has(country.label)) return false;
    seen.add(country.label);
    return true;
  });
}

function resolveUnsupportedCountry(query) {
  const normalized = normalizeCountry(query);
  if (!normalized) return null;

  for (const country of UNSUPPORTED_COUNTRIES) {
    for (const alias of country.aliases) {
      if (countryMatches(normalized, alias)) {
        return country.display;
      }
    }
  }

  return null;
}

function buildUnsupportedMessage(countryName) {
  return UNSUPPORTED_MESSAGE.replaceAll('{country}', countryName);
}

function resolveSearchableCountry(query, searchableCountries) {
  const normalized = normalizeCountry(query);
  if (!normalized) return null;

  for (const country of searchableCountries) {
    for (const term of country.searchTerms) {
      if (countryMatches(normalized, term)) {
        return country;
      }
    }
  }

  return null;
}

function parseRatesBody(body) {
  return body
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.lastIndexOf(':');
      if (separatorIndex === -1) {
        return { label: line, value: '' };
      }

      return {
        label: line.slice(0, separatorIndex).trim(),
        value: line.slice(separatorIndex + 1).trim(),
      };
    });
}

function enrichSection(section, languageCode = 'en') {
  const heading = resolveSectionHeading(section, languageCode);

  if (section.id === 'intro') {
    return {
      id: section.id,
      type: 'intro',
      heading,
      body: section.body,
    };
  }

  if (section.id === 'rates') {
    return {
      id: section.id,
      type: 'rates',
      heading,
      items: parseRatesBody(section.body),
    };
  }

  if (section.id === 'returns' && section.subsections?.length) {
    const returnsContactUrl = section.contact_url || RETURNS_CONTACT_URL;

    return {
      id: section.id,
      type: 'returns',
      heading,
      contact_url: returnsContactUrl,
      subsections: enrichSubsections(section.subsections, languageCode, returnsContactUrl),
    };
  }

  const collapsibleSection = {
    id: section.id,
    type: COLLAPSIBLE_SECTION_IDS.has(section.id) ? 'collapsible' : 'text',
    heading,
    body: section.body,
  };

  if (section.body_html) {
    collapsibleSection.body_html = section.body_html;
  }

  if (section.cta) {
    collapsibleSection.cta = section.cta;
  }

  if (
    !collapsibleSection.body_html &&
    collapsibleSection.cta?.label &&
    section.body?.includes(collapsibleSection.cta.label)
  ) {
    const url = collapsibleSection.cta.url || RETURNS_CONTACT_URL;
    collapsibleSection.body_html = section.body.replace(
      collapsibleSection.cta.label,
      `<a href="${url}">${collapsibleSection.cta.label}</a>`
    );
  }

  return collapsibleSection;
}

function groupCollapsibleSections(sections) {
  const grouped = [];

  for (const section of sections) {
    const previous = grouped[grouped.length - 1];

    if (section.type === 'collapsible') {
      if (previous?.type === 'collapsible-group') {
        previous.items.push(section);
      } else {
        grouped.push({
          id: 'collapsible-group',
          type: 'collapsible-group',
          items: [section],
        });
      }
      continue;
    }

    grouped.push(section);
  }

  return grouped;
}

function placeWithdrawalAfterReturns(sections) {
  const withdrawalIndex = sections.findIndex((section) => section.id === 'withdrawal');
  const returnsIndex = sections.findIndex((section) => section.id === 'returns');

  if (withdrawalIndex === -1 || returnsIndex === -1 || withdrawalIndex > returnsIndex) {
    return sections;
  }

  const next = [...sections];
  const [withdrawal] = next.splice(withdrawalIndex, 1);
  const updatedReturnsIndex = next.findIndex((section) => section.id === 'returns');
  next.splice(updatedReturnsIndex + 1, 0, withdrawal);
  return next;
}

function enrichSections(sections, languageCode = 'en') {
  return groupCollapsibleSections(
    placeWithdrawalAfterReturns(sections).map((section) => enrichSection(section, languageCode))
  );
}

const TEXT_EN_KNOWN_HEADINGS = {
  'Sales Tax': 'sales_tax',
  'Import Tariffs': 'import_tariffs',
  'Orders over $2,500': 'large_orders',
  'Estimated delivery times': 'delivery_times',
  'Our facility': 'facility',
  'Right of withdrawal': 'withdrawal',
};

const TEXT_EN_RETURNS_SUBSECTIONS = {
  'Condition of returned items': 'condition',
  'Refund or Store Credit': 'refund_credit',
  'Free return shipping label': 'free_return_label',
  Exchanges: 'exchanges',
};

function splitTextEnPart(part) {
  const newlineIndex = part.indexOf('\n');

  if (newlineIndex === -1) {
    return { heading: null, body: part };
  }

  return {
    heading: part.slice(0, newlineIndex).trim(),
    body: part.slice(newlineIndex + 1).trim(),
  };
}

function isTextEnReturnsPart(heading) {
  return (
    heading === 'Returns' ||
    Object.prototype.hasOwnProperty.call(TEXT_EN_RETURNS_SUBSECTIONS, heading)
  );
}

function buildTextEnReturnsSection(returnsParts) {
  const contactLabel = 'Contact us';
  const subsections = returnsParts.map(({ heading, body, part }) => {
    if (heading === 'Returns') {
      return {
        id: 'window',
        body,
        body_html: body.replace(
          contactLabel,
          `<a href="${RETURNS_CONTACT_URL}">${contactLabel}</a>`
        ),
        cta: {
          label: contactLabel,
          url: RETURNS_CONTACT_URL,
        },
      };
    }

    return {
      id: TEXT_EN_RETURNS_SUBSECTIONS[heading],
      heading,
      body,
    };
  });

  return {
    id: 'returns',
    heading: 'Returns',
    contact_url: RETURNS_CONTACT_URL,
    subsections,
    body: returnsParts.map(({ part }) => part).join('\n\n'),
  };
}

function parseTextEnSections(textEn) {
  const parts = textEn.split('\n\n').map((part) => part.trim()).filter(Boolean);

  if (parts.length <= 1) {
    return [{ id: 'content', body: textEn }];
  }

  const sections = [];
  const returnsParts = [];

  parts.forEach((part, index) => {
    if (index === 0) {
      sections.push({ id: 'intro', body: part });
      return;
    }

    if (
      /Fedex|FedEx|Royal Mail/i.test(part) &&
      (/£|\$/.test(part) || splitTextEnPart(part).heading === 'Shipping Rates')
    ) {
      sections.push({ id: 'rates', body: part });
      return;
    }

    const { heading, body } = splitTextEnPart(part);

    if (heading && isTextEnReturnsPart(heading)) {
      returnsParts.push({ heading, body, part });
      return;
    }

    if (heading && Object.prototype.hasOwnProperty.call(TEXT_EN_KNOWN_HEADINGS, heading)) {
      const id = TEXT_EN_KNOWN_HEADINGS[heading];
      const parsedSection = {
        id,
        // Ids with no SECTION_HEADINGS entry are structural: the marker in
        // text_en names the block but nothing is shown above the copy.
        heading: SECTION_HEADINGS[id] ? heading : null,
        body,
      };

      if (id === 'withdrawal') {
        parsedSection.cta = {
          label: WITHDRAWAL_CTA_LABEL,
          url: WITHDRAWAL_FORM_URL,
        };
        parsedSection.body_html = body.replace(/\n\n/g, '<br><br>').replace(/\n/g, '<br>');
      }

      sections.push(parsedSection);
      return;
    }

    sections.push({ id: 'tax_refund', body: part });
  });

  if (returnsParts.length) {
    sections.push(buildTextEnReturnsSection(returnsParts));
  }

  return sections;
}

function mapTranslationSections(translation) {
  const languageCode = translation.code || 'en';

  if (translation.sections?.length) {
    return enrichSections(translation.sections, languageCode);
  }

  if (translation.text) {
    return enrichSections(parseTextEnSections(translation.text), languageCode);
  }

  return [];
}

function mapLanguageTab(translation) {
  return {
    language: translation.language,
    code: translation.code,
    sections: mapTranslationSections(translation),
  };
}

function getEnglishTab(countryEntry, translations) {
  const enTranslation = translations.find((translation) => translation.code === 'en');

  if (enTranslation) {
    return mapLanguageTab(enTranslation);
  }

  if (countryEntry.text_en) {
    return {
      language: 'English (en)',
      code: 'en',
      sections: enrichSections(parseTextEnSections(countryEntry.text_en), 'en'),
    };
  }

  return null;
}

function buildLanguageTabs(localTranslations, english) {
  const tabs = localTranslations.map((translation) => ({
    code: translation.code,
    language: translation.language,
    sections: translation.sections,
  }));

  if (english) {
    tabs.push({
      code: english.code,
      language: english.language,
      sections: english.sections,
    });
  }

  return tabs;
}

function enrichTermsSection(section) {
  return {
    id: section.id,
    heading: section.heading,
    body_html: section.body_html || section.body || '',
  };
}

function getTermsForCountry(termsData, countryName) {
  if (!termsData) {
    return null;
  }

  if (countryName === 'default') {
    const sections = termsData.default?.sections;
    if (!sections?.length) {
      return null;
    }

    return {
      title: termsData.title ?? 'Shipping & Returns Terms',
      sections: sections.map(enrichTermsSection),
    };
  }

  const countryEntry = termsData.countries?.[countryName];
  if (!countryEntry?.sections?.length) {
    return null;
  }

  return {
    title: countryEntry.title ?? termsData.title ?? 'Shipping & Returns Terms',
    sections: countryEntry.sections.map(enrichTermsSection),
  };
}

function getCountryContent(countryEntry, termsData) {
  const translations = countryEntry.translations || [];
  const english = getEnglishTab(countryEntry, translations);
  const localTranslations = translations
    .filter((translation) => translation.code !== 'en')
    .map(mapLanguageTab)
    .filter((translation) => translation.sections.length > 0);

  const tabs = buildLanguageTabs(localTranslations, english);

  if (!tabs.length) {
    return null;
  }

  return {
    title: countryEntry.country,
    tabs,
    terms: getTermsForCountry(termsData, countryEntry.country),
    expandSections: EXPANDED_BY_DEFAULT_COUNTRIES.has(countryEntry.country),
  };
}

function getDefaultContent(deliveryData, termsData) {
  return {
    title: null,
    tabs: [
      {
        code: deliveryData.default.code || 'en',
        language: deliveryData.default.language || 'English (en)',
        sections: enrichSections(deliveryData.default.sections),
      },
    ],
    terms: getTermsForCountry(termsData, 'default'),
  };
}

function filterCountrySuggestions(query, searchableCountries) {
  const normalized = normalizeCountry(query);
  const sorted = [...searchableCountries].sort((a, b) => a.label.localeCompare(b.label));

  if (!normalized) {
    return sorted.map((country) => country.label);
  }

  return sorted
    .filter((country) =>
      country.searchTerms.some(
        (term) => term.startsWith(normalized) || term.includes(normalized)
      )
    )
    .sort((a, b) => {
      const aLabel = normalizeCountry(a.label);
      const bLabel = normalizeCountry(b.label);
      const aStarts = aLabel.startsWith(normalized);
      const bStarts = bLabel.startsWith(normalized);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.label.localeCompare(b.label);
    })
    .map((country) => country.label);
}

function isExactCountrySelection(value, searchableCountries) {
  const normalized = normalizeCountry(value);
  if (!normalized) return false;

  return searchableCountries.some((country) =>
    country.searchTerms.some((term) => term === normalized)
  );
}

function buildRegionFromMatch(match, deliveryData, termsData) {
  if (!match) return null;

  if (match.type === 'country') {
    return getCountryContent(match.entry, termsData);
  }

  if (match.type === 'default') {
    return getDefaultContent(deliveryData, termsData);
  }

  return null;
}

document.addEventListener('alpine:init', () => {
  Alpine.data('deliveryCountryLookup', (sectionId, dataUrl, termsDataUrl) => ({
    sectionId,
    dataUrl,
    termsDataUrl,
    deliveryData: null,
    termsData: null,
    searchableCountries: [],
    query: '',
    region: null,
    status: 'idle',
    suggestions: [],
    showSuggestions: false,
    activeIndex: -1,
    activeTab: '',

    get listboxId() {
      return `delivery-country-listbox-${this.sectionId}`;
    },

    get tabsId() {
      return `delivery-country-tabs-${this.sectionId}`;
    },

    resetActiveTab() {
      const tabs = this.region?.tabs;
      this.activeTab = tabs?.length ? tabs[0].code : '';
    },

    async init() {
      try {
        const [deliveryResponse, termsResponse] = await Promise.all([
          fetch(this.dataUrl),
          fetch(this.termsDataUrl),
        ]);
        this.deliveryData = await deliveryResponse.json();
        this.termsData = await termsResponse.json();
        this.searchableCountries = buildSearchableCountries(this.deliveryData);
      } catch (error) {
        console.error('Failed to load delivery country data', error);
      }
    },

    updateSuggestions() {
      if (!this.searchableCountries.length) return;

      this.suggestions = filterCountrySuggestions(this.query, this.searchableCountries);
      this.showSuggestions = this.suggestions.length > 0;
      this.activeIndex = this.suggestions.length ? 0 : -1;
    },

    onInput() {
      if (!this.query.trim()) {
        this.region = null;
        this.status = 'idle';
        this.activeTab = '';
        this.updateSuggestions();
        return;
      }

      this.updateSuggestions();

      if (isExactCountrySelection(this.query, this.searchableCountries)) {
        this.lookup();
        this.showSuggestions = false;
      } else {
        this.region = null;
        this.status = 'idle';
      }
    },

    onFocus() {
      this.updateSuggestions();
    },

    selectCountry(country) {
      this.query = country;
      this.showSuggestions = false;
      this.activeIndex = -1;
      this.lookup();
    },

    highlightNext() {
      if (!this.showSuggestions || !this.suggestions.length) return;
      this.activeIndex = (this.activeIndex + 1) % this.suggestions.length;
    },

    highlightPrev() {
      if (!this.showSuggestions || !this.suggestions.length) return;
      this.activeIndex =
        this.activeIndex <= 0 ? this.suggestions.length - 1 : this.activeIndex - 1;
    },

    selectHighlighted() {
      if (this.showSuggestions && this.activeIndex >= 0) {
        this.selectCountry(this.suggestions[this.activeIndex]);
        return;
      }

      if (this.query.trim()) {
        this.lookup();
        this.showSuggestions = false;
      }
    },

    lookup() {
      const unsupportedCountry = resolveUnsupportedCountry(this.query);
      if (unsupportedCountry) {
        this.region = {
          message: buildUnsupportedMessage(unsupportedCountry),
        };
        this.status = 'unsupported';
        this.activeTab = '';
        return;
      }

      if (!this.deliveryData) return;

      const match =
        this.searchableCountries.find((country) => country.label === this.query) ||
        resolveSearchableCountry(this.query, this.searchableCountries);

      if (!match) {
        this.region = getDefaultContent(this.deliveryData, this.termsData);
        this.status = 'default';
        this.resetActiveTab();
        return;
      }

      if (match.type === 'unsupported') {
        this.region = {
          message: buildUnsupportedMessage(match.label),
        };
        this.status = 'unsupported';
        this.activeTab = '';
        return;
      }

      this.region = buildRegionFromMatch(match, this.deliveryData, this.termsData);
      this.status = match.type;
      this.resetActiveTab();
    },

    clear() {
      this.query = '';
      this.region = null;
      this.status = 'idle';
      this.suggestions = [];
      this.showSuggestions = false;
      this.activeIndex = -1;
      this.activeTab = '';
    },
  }));
});
